// Insights articles. Plain Australian English, answer-first, educational only.
// House rules: no em dashes anywhere, no personal financial or tax advice,
// every article carries the general-information disclaimer in the template.

import type { Article } from "./article-types";

export type { Article, ArticleSection } from "./article-types";

const coreArticles: Article[] = [
  {
    slug: "how-much-does-a-bookkeeper-cost-australia",
    title: "How much does a bookkeeper cost in Australia?",
    metaTitle: "How Much Does a Bookkeeper Cost in Australia? (2026)",
    description:
      "Real 2026 bookkeeper rates in Australia: hourly ranges, monthly packages, what actually drives the price, and how to compare quotes properly.",
    category: "Pricing",
    date: "2026-07-25",
    readingMinutes: 7,
    keywords: [
      "bookkeeper cost australia",
      "bookkeeping rates",
      "how much does a bookkeeper charge",
      "monthly bookkeeping packages",
    ],
    intro:
      "There's no rate card for bookkeeping. What you pay comes down to how messy your books are and how much of the job you're prepared to hand over. Billed hourly, you're generally looking at $60 to $120. On a package, it might be a few hundred dollars a month, or a few thousand, depending on scope. Three things swing that number more than anything else: transaction volume, complexity, and how much of the finance function you actually want off your plate. This guide pulls those apart so you can compare quotes properly instead of guessing.",
    sections: [
      {
        type: "h2",
        text: "What bookkeepers actually charge",
      },
      {
        type: "p",
        text: "Rates track experience, registration and location, sure, but most quotes still land in one of a handful of familiar bands. A junior bookkeeper, or one working with an offshore team, sits at the bottom. A senior local bookkeeper, or a registered BAS agent who can legally prepare and lodge your BAS, costs more. Add CPA or CA oversight to the file and you're paying for a strategic layer that basic bookkeeping simply doesn't include.",
      },
      {
        type: "table",
        headers: ["Service level", "Typical range (2026)", "What you get"],
        rows: [
          [
            "Basic transaction coding",
            "$60 to $80 per hour",
            "Data entry, bank reconciliation, records that are genuinely tidy",
          ],
          [
            "Senior bookkeeper or BAS agent",
            "$80 to $120 per hour",
            "Reconciliations, payroll, BAS and IAS prepared and lodged",
          ],
          [
            "Managed monthly engagement",
            "$500 to $2,500+ per month",
            "A defined scope: bookkeeping, payroll, a compliance calendar, reporting",
          ],
          [
            "Outsourced finance function",
            "$2,000 to $6,000+ per month",
            "Everything above, plus accounts management, reporting and strategic input",
          ],
        ],
      },
      {
        type: "p",
        text: "These are market ranges, not a quote for your business specifically. A five-person trades outfit with one entity and simple payroll isn't in the same universe as a fifty-person group juggling multiple entities, progress claims and divisional reporting.",
      },
      {
        type: "h2",
        text: "What actually moves the number",
      },
      {
        type: "ul",
        items: [
          "How many bank lines, bills and invoices roll through each month.",
          "Payroll: headcount, pay frequency and award conditions all add work, and Single Touch Payroll means every run needs to be right, not roughly right.",
          "Entities: every extra company, trust or partnership brings its own reconciliation and its own lodgement.",
          "How clean the file already is. A Xero file that's drifted needs rescue work before anyone can settle into a monthly rhythm.",
          "Reporting: a bank-ready or board-ready report with real commentary takes skill, and skill takes time.",
          "Who's actually doing the work. BAS services can only legally be provided for a fee by a registered BAS agent.",
        ],
      },
      {
        type: "h2",
        text: "Hourly billing versus a fixed monthly fee",
      },
      {
        type: "p",
        text: "Hourly billing feels safe right up until the invoice starts wandering around every month, punishing you for inefficiency you can't even see. A fixed monthly fee flips that: it rewards the bookkeeper for building clean systems and gives you a number you can actually budget against. For an established business, a defined monthly scope wins almost every time. What matters most is that the scope is written down: what's included, what isn't, and when you'll see it.",
      },
      {
        type: "callout",
        title: "The question worth asking before the price",
        text: "Ask any bookkeeper you're considering this: what will I get each month, and on what date? A vague answer makes the price meaningless, because there's nothing left to measure it against.",
      },
      {
        type: "h2",
        text: "Cheap bookkeeping rarely stays cheap",
      },
      {
        type: "p",
        text: "The most expensive bookkeeping most owners ever buy is the cheap kind. GST gets miscoded, super goes out late, deductions get missed, and eventually an accountant charges year-end rates to untangle twelve months of drift. If a quote sits well below the market, something's being cut. Usually it's reconciliation quality, review and the compliance calendar. Which happen to be exactly what you're paying for.",
      },
      {
        type: "h2",
        text: "How NextEra prices it",
      },
      {
        type: "p",
        text: "NextEra works on fixed monthly scopes, agreed after a Strategic Finance Review. That review looks at your transaction volume, payroll, entities, file health and the reporting you actually need, and the proposal spells out exactly what you'll get and when. CPA-led oversight sits across every file, so the books aren't just entered. They're checked.",
      },
    ],
    faq: [
      {
        question: "How much does a bookkeeper charge per hour in Australia?",
        answer:
          "Most sit between $60 and $120 an hour in 2026, with registered BAS agents and senior bookkeepers toward the top and basic transaction coding near the bottom.",
      },
      {
        question: "Is a monthly package better than paying by the hour?",
        answer:
          "For an established business, yes, generally. A package gives you a predictable bill and a defined scope, and it rewards the bookkeeper for working efficiently instead of racking up hours.",
      },
      {
        question: "Who's actually allowed to lodge my BAS?",
        answer:
          "Anyone charging a fee to lodge BAS needs to be a registered BAS agent or tax agent under the Tax Agent Services Act. Check the Tax Practitioners Board register before handing anything over.",
      },
      {
        question: "Why do quotes for the same job vary so much?",
        answer:
          "Because it's rarely the same job. Transaction volume, payroll complexity, how many entities you run and the state of your file all change the workload. Two quotes are only comparable once they spell out exactly what gets delivered each month.",
      },
    ],
    related: [
      "bookkeeper-vs-accountant-australia",
      "signs-you-have-outgrown-your-bookkeeper",
      "management-reports-owners-actually-use",
    ],
  },
  {
    slug: "bookkeeper-vs-accountant-australia",
    title: "Bookkeeper vs accountant: who does what, and who do you need?",
    metaTitle: "Bookkeeper vs Accountant: Who Does What in Australia",
    description:
      "What a bookkeeper does, what an accountant does, where a registered BAS agent fits, and why most Australian businesses need both.",
    category: "Foundations",
    date: "2026-07-25",
    readingMinutes: 6,
    keywords: [
      "bookkeeper vs accountant",
      "difference between bookkeeper and accountant australia",
      "bas agent vs tax agent",
      "do i need a bookkeeper or accountant",
    ],
    intro:
      "Ever wondered why your bookkeeper and your accountant seem to be doing two completely different jobs? That's because they are. A bookkeeper keeps your financial records accurate and current, week to week. An accountant interprets those records for tax, structure and strategy, usually quarterly or annually. Most established businesses need both, and the ones that run best treat the two as one coordinated team, not two suppliers who never talk to each other.",
    sections: [
      {
        type: "h2",
        text: "What a bookkeeper does",
      },
      {
        type: "ul",
        items: [
          "Records and codes every transaction, and reconciles bank, card and loan accounts",
          "Runs payroll, super and Single Touch Payroll reporting",
          "Manages accounts payable and receivable so cash keeps moving",
          "Prepares BAS and IAS (lodging it requires a registered BAS or tax agent)",
          "Keeps the file clean so every other decision rests on real numbers",
        ],
      },
      {
        type: "h2",
        text: "What an accountant does",
      },
      {
        type: "ul",
        items: [
          "Prepares financial statements and income tax returns",
          "Advises on business structure, tax planning and compliance strategy",
          "Handles ATO matters that sit outside BAS agent scope",
          "Weighs in on the big decisions: acquisitions, finance, succession",
        ],
      },
      {
        type: "h2",
        text: "Where a BAS agent fits",
      },
      {
        type: "p",
        text: "A registered BAS agent is a bookkeeper who's met the Tax Practitioners Board's education and experience requirements and is legally allowed to provide BAS services for a fee: preparing and lodging activity statements, advising on GST, PAYG withholding and super guarantee obligations. If someone's charging you to lodge BAS and isn't registered, that's a red flag with real teeth.",
      },
      {
        type: "h2",
        text: "The expensive gap between the two",
      },
      {
        type: "p",
        text: "Most financial pain in small business lives in the gap between the bookkeeper and the accountant. The bookkeeper assumes the accountant will catch anything at year end. The accountant assumes the file they're handed is right. Nobody owns the monthly rhythm, and the owner finds out about a problem nine months after it started. The fix isn't a better bookkeeper or a better accountant. It's a defined process: who does what, delivered when, reviewed by whom.",
      },
      {
        type: "callout",
        title: "A useful test",
        text: "Ask yourself this: if the ATO queried last quarter today, who on your finance team would answer it, and how fast? If the honest answer is 'my accountant, eventually, at year-end rates', that gap is already costing you.",
      },
      {
        type: "h2",
        text: "Where NextEra sits in between",
      },
      {
        type: "p",
        text: "NextEra runs the bookkeeping side as a managed monthly function with CPA-led oversight, then works directly with your accountant: clean files handed over, questions answered through the year, no year-end archaeology. Your accountant does higher value work in less time, and that usually shows up in their bill too.",
      },
    ],
    faq: [
      {
        question: "Do I need both a bookkeeper and an accountant?",
        answer:
          "Most established Australian businesses do. The bookkeeper keeps things accurate through the year; the accountant turns that into tax, compliance and advice.",
      },
      {
        question: "Can a bookkeeper lodge my BAS?",
        answer:
          "Only if they're a registered BAS agent, or working under one. You can check registration on the Tax Practitioners Board's public register.",
      },
      {
        question: "Is a CPA a bookkeeper or an accountant?",
        answer:
          "CPA is a professional accounting designation, not a job title in the bookkeeping sense. A CPA leading a bookkeeping team means the file gets accountant-level review all year, not just at tax time.",
      },
      {
        question: "Will good bookkeeping reduce my accounting fees?",
        answer:
          "Often, yes. A clean, reconciled file means your accountant spends less time fixing records and more time on tax planning and advice, which is a better use of their higher hourly rate.",
      },
    ],
    related: [
      "how-much-does-a-bookkeeper-cost-australia",
      "xero-file-health-check",
      "bas-due-dates-australia",
    ],
  },
  {
    slug: "bas-due-dates-australia",
    title: "BAS due dates in Australia: the calendar every business owner should know",
    metaTitle: "BAS Due Dates Australia: Quarterly and Monthly Deadlines",
    description:
      "Standard quarterly and monthly BAS due dates, how registered agent lodgement extends them, and a system for never lodging late.",
    category: "Compliance",
    date: "2026-07-25",
    readingMinutes: 5,
    keywords: [
      "bas due dates",
      "quarterly bas due dates australia",
      "when is my bas due",
      "bas lodgement deadlines",
    ],
    intro:
      "Nobody enjoys BAS season, and the due dates have a habit of sneaking up every single quarter. For quarterly lodgers, the standard dates are 28 October, 28 February, 28 April and 28 July. Monthly BAS is generally due on the 21st of the following month. Lodge through a registered BAS or tax agent and you'll usually get extra time on most quarters. Here's the full picture, plus the system that makes the dates irrelevant because you're simply never late.",
    sections: [
      {
        type: "h2",
        text: "Standard quarterly BAS due dates",
      },
      {
        type: "table",
        headers: ["Quarter", "Period", "Standard due date"],
        rows: [
          ["Q1", "July to September", "28 October"],
          ["Q2", "October to December", "28 February"],
          ["Q3", "January to March", "28 April"],
          ["Q4", "April to June", "28 July"],
        ],
      },
      {
        type: "p",
        text: "When a due date falls on a weekend or public holiday, you can lodge and pay on the next business day. Q2 already carries a built-in extension over the summer break, which is why it stretches out to 28 February.",
      },
      {
        type: "h2",
        text: "The agent lodgement advantage",
      },
      {
        type: "p",
        text: "Businesses that lodge through a registered BAS or tax agent generally get concessional due dates, typically an extra four weeks on most quarters under the ATO's lodgement program. The exact concession depends on the quarter and your circumstances, so treat your agent's compliance calendar as the source of truth, not this article. The bigger benefit isn't even the extra time. It's that lodgement stops depending on your memory.",
      },
      {
        type: "h2",
        text: "Monthly BAS and IAS",
      },
      {
        type: "p",
        text: "Businesses with GST turnover of $20 million or more must lodge monthly, and some smaller businesses choose it anyway for the cash flow discipline. Monthly activity statements are generally due on the 21st of the following month. Employers withholding PAYG also lodge IAS in the months between quarterly BAS lodgements.",
      },
      {
        type: "h2",
        text: "Why businesses lodge late (hint: it's rarely the date)",
      },
      {
        type: "ul",
        items: [
          "Bank feeds haven't been reconciled, so the GST figures can't be trusted",
          "Payroll and super don't match the ledger, so someone has to investigate before lodging",
          "Receipts and bills are missing, so coding turns into guesswork",
          "Nobody owns the deadline: the owner assumes the bookkeeper has it, the bookkeeper assumes the accountant does",
        ],
      },
      {
        type: "p",
        text: "Every one of those is a process problem dressed up as a calendar problem. A file reconciled weekly is always just days from a lodgeable BAS, and that turns the due date into a non-event.",
      },
      {
        type: "callout",
        title: "General information only",
        text: "Due dates and concessions can change and depend on your registration and circumstances. Confirm your dates with the ATO or your registered agent; this article is general information, not tax advice.",
      },
      {
        type: "h2",
        text: "How NextEra keeps it boring",
      },
      {
        type: "p",
        text: "NextEra clients run on a documented compliance calendar: reconciliations weekly, payroll checked every run, BAS prepared and reviewed ahead of the concession date, then lodged by a registered agent. You see the whole year of dates upfront, and you're told the moment each obligation is done.",
      },
    ],
    faq: [
      {
        question: "What are the quarterly BAS due dates?",
        answer:
          "28 October for July to September, 28 February for October to December, 28 April for January to March, and 28 July for April to June. Weekend and public holiday due dates roll to the next business day.",
      },
      {
        question: "Do I get more time if I lodge through an agent?",
        answer:
          "Generally, yes. Lodging through a registered BAS or tax agent usually means concessional due dates, often around four extra weeks on most quarters, though your agent will confirm the exact dates for your situation.",
      },
      {
        question: "What happens if I lodge my BAS late?",
        answer:
          "The ATO can apply failure to lodge penalties and interest, and it can complicate payment plan negotiations too. If you're already behind, lodging promptly, even with a payment arrangement in place, beats waiting almost every time.",
      },
      {
        question: "When is monthly BAS due?",
        answer: "Generally the 21st of the month after the reporting period.",
      },
    ],
    related: [
      "bookkeeper-vs-accountant-australia",
      "xero-file-health-check",
      "how-much-does-a-bookkeeper-cost-australia",
    ],
  },
  {
    slug: "signs-you-have-outgrown-your-bookkeeper",
    title: "7 signs your business has outgrown its bookkeeper",
    metaTitle: "7 Signs Your Business Has Outgrown Its Bookkeeper",
    description:
      "Late reports, surprise tax bills and payroll fixes: the signs an established business needs a finance function, not just data entry.",
    category: "Growth",
    date: "2026-07-25",
    readingMinutes: 6,
    keywords: [
      "outgrown bookkeeper",
      "when to change bookkeeper",
      "bookkeeper not keeping up",
      "upgrade bookkeeping",
    ],
    intro:
      "Picture this: the bookkeeper who was perfect for your business at $500k of revenue is now the bottleneck at $2 million. They haven't got worse. The job changed underneath them. If more than two of the seven signs below sound familiar, the problem probably isn't effort. It's that your business now needs a finance function, not just data entry.",
    sections: [
      {
        type: "h2",
        text: "1. You find out about problems at year end",
      },
      {
        type: "p",
        text: "Your accountant flags misallocated GST, a loan account that doesn't reconcile, or super paid late, and it's the first you're hearing of it, nine months after it happened. Recording transactions isn't the same as reviewing them. Growing files need someone checking the work every month, not once a year.",
      },
      {
        type: "h2",
        text: "2. Reports arrive late, or not at all",
      },
      {
        type: "p",
        text: "If the monthly figures land in week four, they're history by the time you read them. An established business should see profit and loss, cash and debtor positions within the first half of the following month, every month, on a date that doesn't move.",
      },
      {
        type: "h2",
        text: "3. You still do finance admin yourself",
      },
      {
        type: "p",
        text: "Chasing invoices at 9pm. Approving bills from your phone with no workflow behind it. Fielding payroll questions between jobs. Owner time is the most expensive labour in the business, and finance admin is the easiest place to claw some of it back.",
      },
      {
        type: "h2",
        text: "4. Payroll keeps needing small fixes",
      },
      {
        type: "p",
        text: "An adjustment here, a super catch-up there: small payroll errors are the early warning sign of bigger process problems. With Single Touch Payroll and super guarantee obligations in play, payroll is the worst place in the business to be nearly right.",
      },
      {
        type: "h2",
        text: "5. Nobody explains what the numbers mean",
      },
      {
        type: "p",
        text: "You get the reports, sure, but no commentary. Margin drops two points and nobody says why. The step up from bookkeeping to a real finance function is exactly this: someone whose job includes telling you what changed, what to watch, and what to ask next.",
      },
      {
        type: "h2",
        text: "6. Your setup can't answer new questions",
      },
      {
        type: "p",
        text: "Which division is actually profitable? What does this job really cost? Can you afford the new hire? If the chart of accounts and tracking were built for a smaller business, the file genuinely can't answer the questions you need answered now.",
      },
      {
        type: "h2",
        text: "7. Everything lives in one person's head",
      },
      {
        type: "p",
        text: "One bookkeeper, no documentation, no backup. When they're sick or they leave, your finance function stops with them. A process that's written down and delivered by a team doesn't have a single point of failure.",
      },
      {
        type: "callout",
        title: "What the next level looks like",
        text: "A defined monthly scope. Weekly reconciliations. Payroll reviewed every run. Reports on a fixed date with commentary in plain language. A compliance calendar someone else owns. CPA oversight across the lot.",
      },
      {
        type: "h2",
        text: "Making the switch without the drama",
      },
      {
        type: "p",
        text: "Outgrowing a bookkeeper is normal, and it isn't a betrayal of anyone. The transition is straightforward when it's structured properly: review the current file, define the new scope, hand over access, run the first month in parallel. NextEra's Strategic Finance Review is built to map exactly that path before you commit to anything.",
      },
    ],
    faq: [
      {
        question: "How do I change bookkeepers without disruption?",
        answer:
          "Structure the handover properly: review the file first, put the new scope in writing, transfer software access and authorities, then run the first month with extra checking. A good incoming provider manages most of this for you.",
      },
      {
        question: "Should I hire an in-house bookkeeper instead?",
        answer:
          "In-house works when there's a genuine full-time workload and someone senior enough to review the work. Plenty of businesses between $1 million and $20 million find an outsourced finance function delivers senior review and team coverage for less than one full-time salary.",
      },
      {
        question: "What should monthly reporting include?",
        answer:
          "At minimum: profit and loss against budget or prior period, balance sheet, cash position and movement, debtors and creditors, and a short plain-language note on what changed and why.",
      },
    ],
    related: [
      "management-reports-owners-actually-use",
      "how-much-does-a-bookkeeper-cost-australia",
      "xero-file-health-check",
    ],
  },
  {
    slug: "xero-file-health-check",
    title: "How to run a Xero file health check (10 checks in 30 minutes)",
    metaTitle: "Xero File Health Check: 10 Checks in 30 Minutes",
    description:
      "A practical ten point Xero health check: reconciliations, suspense accounts, GST settings, payroll, super and aged reports.",
    category: "Xero",
    date: "2026-07-25",
    readingMinutes: 7,
    keywords: [
      "xero health check",
      "xero file review",
      "clean up xero",
      "xero bookkeeping checklist",
    ],
    intro:
      "Most Xero files don't break loudly, and that's exactly the problem. They drift: a few unreconciled lines here, a growing suspense account there, a bank feed that quietly skipped a fortnight. Run these ten checks and you'll know inside thirty minutes whether your file is telling you the truth. No accounting degree required.",
    sections: [
      {
        type: "h2",
        text: "The 10 checks",
      },
      {
        type: "h3",
        text: "1. Bank accounts actually reconcile",
      },
      {
        type: "p",
        text: "For each bank account, compare the statement balance to what Xero shows on the same date. If they don't match, the file's out of sync with reality, and every report built on top of it inherits that error.",
      },
      {
        type: "h3",
        text: "2. No stale unreconciled lines",
      },
      {
        type: "p",
        text: "A handful of recent unreconciled lines is completely normal. Lines that have sat there for months usually mean something's unrecorded, or duplicated somewhere else in the file.",
      },
      {
        type: "h3",
        text: "3. The suspense account is empty",
      },
      {
        type: "p",
        text: "Whatever yours is called, suspense, ask my accountant, unknown, it should trend toward zero. A balance that keeps growing is really just a pile of decisions nobody's made.",
      },
      {
        type: "h3",
        text: "4. GST settings match your registration",
      },
      {
        type: "p",
        text: "The accounting basis (cash or accruals) and reporting frequency set in Xero need to match your actual ATO registration. Get this wrong and you'll produce incorrect BAS figures with complete, unearned confidence.",
      },
      {
        type: "h3",
        text: "5. Aged receivables are real",
      },
      {
        type: "p",
        text: "Scan the aged receivables report. Invoices that will never be paid, duplicates, test invoices: they all inflate your revenue and your debtors. A clean list gets chased. A fantasy list gets ignored.",
      },
      {
        type: "h3",
        text: "6. Aged payables are real",
      },
      {
        type: "p",
        text: "Run the same test on bills. Anything old and unrecognisable is usually a duplicate or an entry error, and it distorts what you appear to owe.",
      },
      {
        type: "h3",
        text: "7. Payroll matches the ledger",
      },
      {
        type: "p",
        text: "Compare a payroll activity summary against the wages, PAYG withholding and super accounts in the profit and loss and balance sheet. Any difference means a pay run got edited or posted somewhere outside payroll.",
      },
      {
        type: "h3",
        text: "8. Super is paid, not just recorded",
      },
      {
        type: "p",
        text: "The super payable account should clear to close to zero shortly after each payment deadline. A balance that only ever grows is one of the most expensive problems in Australian small business, full stop.",
      },
      {
        type: "h3",
        text: "9. Loan balances match statements",
      },
      {
        type: "p",
        text: "Every loan and finance balance on the balance sheet should match the lender's statement. Drift here usually means repayments are coded to the wrong place, which quietly misstates profit.",
      },
      {
        type: "h3",
        text: "10. Users and access make sense",
      },
      {
        type: "p",
        text: "Check who has access to the file, and at what level. Former staff and old advisers should be gone, and payroll admin should be limited to the people who genuinely need it.",
      },
      {
        type: "callout",
        title: "Scoring it",
        text: "Ten passes and your file's in good shape. One or two fails and it's fixable this month. Three or more and the file needs a structured cleanup before you can trust anything built on top of it.",
      },
      {
        type: "h2",
        text: "When it's time to call someone in",
      },
      {
        type: "p",
        text: "If the checks turned up problems you can't explain, a proper file review goes deeper: chart of accounts structure, tracking categories, historical corrections, and the process gaps that let the drift happen in the first place. NextEra runs this as part of every new engagement, and a standalone Xero health check is also available.",
      },
    ],
    faq: [
      {
        question: "How often should I check my Xero file health?",
        answer:
          "Reconcile weekly, run a structured check like this one quarterly, and get a full professional review annually or whenever you change bookkeepers.",
      },
      {
        question: "What is the most common Xero problem?",
        answer:
          "Unreconciled or force-reconciled bank lines. Once the bank doesn't match reality, GST, profit and pretty much every other report inherit the error.",
      },
      {
        question: "Can NextEra fix a messy Xero file?",
        answer:
          "Yes. File rescues and cleanups are a core service: reconciliations brought current, miscoding corrected, payroll and super verified, and a structure put in place so the file actually stays clean afterwards.",
      },
    ],
    related: [
      "bas-due-dates-australia",
      "signs-you-have-outgrown-your-bookkeeper",
      "bookkeeper-vs-accountant-australia",
    ],
  },
  {
    slug: "management-reports-owners-actually-use",
    title: "Management reports business owners actually use (and the ones they ignore)",
    metaTitle: "Management Reports Business Owners Actually Use",
    description:
      "The monthly management reports worth reading as an owner: profit and loss with commentary, cash view, debtors, and the numbers that drive real decisions.",
    category: "Reporting",
    date: "2026-07-25",
    readingMinutes: 6,
    keywords: [
      "management reports small business",
      "monthly business reports",
      "management reporting australia",
      "what reports should my bookkeeper send",
    ],
    intro:
      "Most management reports get ignored, and it's not because owners are lazy. It's because the reports are built for accountants, not for owners. The ones that actually get read share three things: they arrive on a fixed date, they compare against something, and a human explains what changed in plain language. Here's the monthly pack that earns its place on your desk, and the ones you can stop pretending to read.",
    sections: [
      {
        type: "h2",
        text: "The pack that gets read",
      },
      {
        type: "h3",
        text: "Profit and loss, compared",
      },
      {
        type: "p",
        text: "A profit and loss on its own is just a number floating in space. Set against budget and the same month last year, it turns into a story: revenue up, margin down two points, wages ratio creeping. The comparison is the report; the number by itself is nothing.",
      },
      {
        type: "h3",
        text: "A cash view you can actually act on",
      },
      {
        type: "p",
        text: "Where cash started, where it ended, and the three or four movements that explain the gap: tax paid, stock bought, loan repaid, debtor collected. For most businesses this one page beats a full statutory cash flow statement when it comes to actually making decisions.",
      },
      {
        type: "h3",
        text: "Debtors with names on them",
      },
      {
        type: "p",
        text: "Not just an ageing summary. The actual five biggest overdue accounts, who's chasing them, and what's been promised. Cash collection improves the moment a report starts naming names.",
      },
      {
        type: "h3",
        text: "Commentary in plain language",
      },
      {
        type: "p",
        text: "Five to ten sentences from someone who actually knows the file: what moved, why, what to watch next month, and any question that needs an owner's decision. This is the bit that turns data into management.",
      },
      {
        type: "h2",
        text: "The reports owners ignore (and rightly so)",
      },
      {
        type: "ul",
        items: [
          "A twelve-page general ledger dump nobody asked for",
          "A balance sheet with zero explanation of what changed",
          "Charts of every metric the software can spit out, signifying nothing",
          "Anything that arrives so late the month's already history",
        ],
      },
      {
        type: "h2",
        text: "Timing beats perfection",
      },
      {
        type: "p",
        text: "A good report on the 10th beats a perfect one on the 25th, every time. Decisions have a shelf life. The discipline behind early reporting is boring and completely unglamorous: reconciliations done weekly, payroll checked every run, a close checklist someone actually owns.",
      },
      {
        type: "callout",
        title: "One question to ask your bookkeeper",
        text: "What date each month will my reports land, and what will they include? No fixed answer means you don't have management reporting. You have accounting archaeology.",
      },
      {
        type: "h2",
        text: "What you get with NextEra",
      },
      {
        type: "p",
        text: "NextEra's Insights and Tailored Finance Function clients get a fixed-date monthly pack: compared profit and loss, cash view, named debtors and plain-language commentary, built on a file reconciled weekly and reviewed under CPA oversight. Divisional, job or entity layers get added wherever the business actually needs them.",
      },
    ],
    faq: [
      {
        question: "What management reports should a small business get monthly?",
        answer:
          "At minimum: profit and loss against budget or last year, a cash position and movement summary, aged debtors and creditors, and short written commentary on what changed.",
      },
      {
        question: "When should monthly reports arrive?",
        answer:
          "Within the first half of the following month, on a fixed date, because reports lose their decision value fast and a reliable mid-month report beats a perfect late one.",
      },
      {
        question: "What is the difference between management reports and financial statements?",
        answer:
          "Financial statements are formal, backward-looking documents built for compliance. Management reports are internal tools built for decisions: comparative, timely, and explained in plain language.",
      },
    ],
    related: [
      "signs-you-have-outgrown-your-bookkeeper",
      "how-much-does-a-bookkeeper-cost-australia",
      "xero-file-health-check",
    ],
  },
  {
    slug: "single-touch-payroll-guide-for-employers",
    title: "Single Touch Payroll: what every Australian employer needs to have right",
    metaTitle: "Single Touch Payroll: An Employer's Guide",
    description:
      "What STP reports to the ATO, STP Phase 2 categories, super guarantee timing, and the habits that keep Australian payroll clean and compliant.",
    category: "Payroll",
    date: "2026-07-25",
    readingMinutes: 6,
    keywords: [
      "single touch payroll",
      "stp phase 2",
      "payroll compliance australia",
      "super guarantee obligations",
    ],
    intro:
      "Single Touch Payroll means the ATO sees your payroll data the moment you pay your team, not once a year at tax time. That's fine when payroll's right, and uncomfortable when it's nearly right. Here's what STP actually reports, where employers commonly slip up, and the habits that keep payroll boring, in the best possible way.",
    sections: [
      {
        type: "h2",
        text: "What STP reports to the ATO",
      },
      {
        type: "p",
        text: "Every pay event sends salaries and wages, PAYG withholding and superannuation liability information for each employee, itemised under STP Phase 2 into detailed categories: gross, overtime, allowances, bonuses, leave types, salary sacrifice, among others. The ATO reconciles all of it against your activity statements and your super fund payments, automatically.",
      },
      {
        type: "h2",
        text: "STP Phase 2, in one paragraph",
      },
      {
        type: "p",
        text: "Phase 2 expanded exactly how much detail employers have to report: income types, disaggregated gross pay components, employment basis and tax treatment codes. In practice, it means your payroll categories need to be set up correctly, because a wage lumped into the wrong category is now a reporting error the ATO can see, not just an internal untidiness nobody notices.",
      },
      {
        type: "h2",
        text: "Super guarantee: the deadline that actually bites",
      },
      {
        type: "p",
        text: "The super guarantee rate is 12% of ordinary time earnings, having reached 12% from 1 July 2025. Payments are currently due quarterly, and late super is uniquely painful: the super guarantee charge kills deductibility, adds interest and administration fees, and comes with its own ATO paperwork. Paying super on time is one of the highest-return habits in Australian payroll, full stop. Note that payday super has been legislated from July 2026, so keep an eye on how the timing rules apply to your business as guidance rolls out.",
      },
      {
        type: "h2",
        text: "The five payroll errors we see most",
      },
      {
        type: "ul",
        items: [
          "Wrong award interpretation: base rates fine, allowances and penalties wrong",
          "Payroll categories mapped to the wrong STP Phase 2 fields",
          "Super calculated on the wrong earnings base",
          "Manual edits to pay runs that never make it back to the ledger, so payroll and accounts disagree",
          "Terminations processed without the right final pay, leave payout or reporting treatment",
        ],
      },
      {
        type: "h2",
        text: "Habits that keep payroll clean",
      },
      {
        type: "ul",
        items: [
          "Reconcile payroll to the ledger every run, not once a quarter",
          "Review one pay run a month in real detail, including a spot check against the award",
          "Clear the super payable account against actual fund payments, and keep the evidence",
          "Document the payroll process so it survives staff changes",
          "Finalise STP promptly at year end so employees' tax records come out right",
        ],
      },
      {
        type: "callout",
        title: "General information only",
        text: "Payroll obligations depend on your awards, agreements and circumstances, and the rules do change. Confirm specifics with the ATO, Fair Work or your registered agent; this article is general information, not advice.",
      },
      {
        type: "h2",
        text: "Where NextEra fits",
      },
      {
        type: "p",
        text: "NextEra manages payroll end to end for established businesses: documented pay run processes, STP reporting, super tracked right through to payment, leave and entitlements checked, reconciliation to the ledger every cycle. Payroll questions get answered by someone who actually knows your file, not a call centre.",
      },
    ],
    faq: [
      {
        question: "What is Single Touch Payroll?",
        answer:
          "STP is the ATO's payroll reporting system. Employers report wages, PAYG withholding and super liability information electronically with every pay run, through STP-enabled software such as Xero.",
      },
      {
        question: "What's the current super guarantee rate?",
        answer: "12% of ordinary time earnings, having reached that rate from 1 July 2025.",
      },
      {
        question: "What happens if I pay super late?",
        answer:
          "It triggers the super guarantee charge: the amount stops being tax deductible, interest and administration fees apply, and you have to lodge a super guarantee charge statement with the ATO. Avoiding late super is one of the most valuable habits in payroll.",
      },
      {
        question: "Do small employers have to use STP?",
        answer:
          "Yes. STP applies to employers of every size in Australia, with only narrow exemptions, and any STP-enabled payroll software handles the reporting as part of each pay run.",
      },
    ],
    related: [
      "xero-file-health-check",
      "bas-due-dates-australia",
      "how-much-does-a-bookkeeper-cost-australia",
    ],
  },
];

import { complianceOperationsArticles } from "./articles/compliance-operations";
import { payrollGrowthArticles } from "./articles/payroll-growth";
import { xeroAiArticles } from "./articles/xero-ai";
import { moneyIndustriesArticles } from "./articles/money-industries";

export const articles: Article[] = [
  ...coreArticles,
  ...xeroAiArticles,
  ...complianceOperationsArticles,
  ...payrollGrowthArticles,
  ...moneyIndustriesArticles,
];

export function getArticle(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug);
}
